import { AudiencePageLayout, type AudienceContent } from './AudiencePageLayout';

const content: Record<'de' | 'en', AudienceContent> = {
  de: {
    title: 'qtplan – Projektplanung für Freelancer | Board, Roadmap & PERT',
    metaDesc: 'Board für Projekt-Kickoff mit dem Kunden, Roadmap für die Präsentation, Übersicht für fundierte Angebote. PERT-Schätzungen, Canvas, lokal und kostenlos.',
    heroImg: { src: '/img/remote-work.jpg', pos: '50% 65%' },
    hero: {
      h1: 'Kickoff auf dem Board. Roadmap für den Kunden. Angebot mit der Übersicht.',
      lead: 'Professionelle Planung ohne Enterprise-Preise. qtplan liefert ein Board für den Projekt-Kickoff mit dem Kunden, eine Gantt-Roadmap für die Präsentation und eine Übersicht mit allen Konsequenzen für fundierte Angebote. Kostenlos, lokal, ohne Registrierung.',
    },
    bento: {
      heading: 'Drei Ansichten für Freelancer',
      items: [
        {
          span: 2,
          label: 'BOARD',
          title: 'Projekt-Kickoff auf dem Board — gemeinsam mit dem Kunden',
          body: 'Am Anfang jedes Auftrags: Scope klären, Aufwand schätzen, Risiken identifizieren. Auf dem Board wird die Discovery als Freifläche — Tasks strukturieren, PERT-Schätzungen erfassen, Requirements und Konsequenzen dokumentieren. Das Board kann direkt im Kundengespräch gezeigt werden. Was als grobe Struktur beginnt, wird iterativ zum Projektplan weiterentwickelt statt weggeworfen.',
          variant: 'accent',
        },
        {
          span: 1,
          label: 'ROADMAP',
          title: 'Gantt-Timeline die Kunden beeindruckt',
          body: 'Dieselben Daten als Gantt-Timeline — die Roadmap für den Kunden. Wann wird was fertig, wo liegen Risiken, wie wirken sich Änderungen aus. Eine interaktive Zeitachse statt einer Textliste. Kein Extra-Tool, kein Nachpflegen.',
        },
        {
          span: 1,
          label: 'ANGEBOT',
          title: 'Fundierte Angebote mit der aggregierten Übersicht',
          body: 'Die Übersicht fasst alle Requirements, Deadlines und Konsequenzen zusammen. "Mit 85% Wahrscheinlichkeit dauert das Projekt 3 bis 5 Wochen, das Hauptrisiko liegt in der API-Integration" — das ist professionell, nachvollziehbar und schützt vor zu optimistischen Angeboten die zu unbezahlter Mehrarbeit führen.',
        },
        {
          span: 2,
          label: 'SCHÄTZUNG',
          title: 'PERT-Schätzungen die Vertrauen schaffen',
          body: 'Kunden erwarten präzise Zeitangaben — und genau hier scheitern die meisten Freelancer. Ohne Methodik bleiben Schätzungen ungenau. qtplan nutzt die PERT-Methodik: drei Schätzwerte pro Task (optimistisch, realistisch, pessimistisch), daraus Erwartungswert E = (O + 4M + P) / 6 und Unsicherheit. Auf dem Board erfasst, in der Roadmap sichtbar, in der Übersicht als Risiko aggregiert. Das unterscheidet professionelle Consultants von Gelegenheitsfreiberuflern.',
        },
        {
          span: 1,
          label: 'PROJEKTE',
          title: 'Multi-Board für parallele Aufträge',
          body: 'Zwei bis drei parallele Aufträge sind normal. Jeder Kunde bekommt ein eigenes Board. Der Wechsel dauert Sekunden. Die boardübergreifende Doppelbuchungs-Erkennung zeigt wo sich Zeiträume überlappen — bevor einem Kunden ein unrealistischer Termin zugesagt wird.',
          variant: 'accent',
        },
        {
          span: 1,
          label: 'CANVAS',
          title: 'Projektstruktur für Kickoffs visualisieren',
          body: 'Der Canvas bietet eine Freifläche mit Layern, Gruppen, Verbindungen und einem Sprite-Katalog. Für Kickoffs um gemeinsam mit dem Kunden die Struktur zu erarbeiten. Tech-Trees, Abhängigkeiten, Infrastruktur — als lebendiges Diagramm statt einer statischen Folie.',
        },
        {
          span: 2,
          label: 'LOKAL',
          title: 'Kostenlos, lokal und unbegrenzt nutzbar',
          body: 'qtplan läuft komplett im Browser. Keine Cloud, kein Backend, kein Registrierungszwang, keine Objektlimits. Projektdaten und Schätzungen verlassen nie den Rechner. Keine monatlichen Kosten, kein Vendor Lock-in. Boards lassen sich als JSON exportieren — als Backup oder zum Teilen mit Kunden.',
          variant: 'muted',
        },
        {
          span: 1,
          label: 'WORKFLOW',
          title: 'Konfigurierbare Spalten für den eigenen Prozess',
          body: 'Flexible Spaltenstruktur pro Board — von Backlog über In Progress bis Done, oder eigene Kategorien wie Review, Kundenfeedback oder Deployment. Jede Spalte kann Kategorien zugeordnet werden, der Task-Pool filtert automatisch.',
        },
      ],
    },
    narrative: {
      heading: 'Warum Freelancer professionelle Planungswerkzeuge brauchen',
      paragraphs: [
        'Freelancer stehen vor einem Paradox: Kunden erwarten die gleiche Professionalität wie von einer Agentur — aber das Budget für Enterprise-Tools fehlt, und diese Tools liefern Features die für Einzelpersonen irrelevant sind. Das Ergebnis: viele Freelancer planen mit Notiz-Apps, Tabellen oder gar nicht.',
        'In qtplan startet alles auf dem Board. Als Freifläche — gemeinsam mit dem Kunden den Scope klären, Tasks strukturieren, Risiken aufdecken. PERT-Schätzungen geben eine methodisch fundierte Grundlage für Angebote. Statt "das dauert ungefähr vier Wochen" heißt es "mit 85% Wahrscheinlichkeit liegt der Aufwand zwischen drei und fünf Wochen".',
        'Für den Kunden werden dieselben Daten zur Gantt-Timeline — der Roadmap. Ein interaktiver Zeitplan statt statischer Tabellen. Verschiebungen und ihre Auswirkungen sind sofort sichtbar. Die aggregierte Übersicht fasst Requirements und Konsequenzen zusammen — als Grundlage für das Angebot oder direkt im Meeting.',
        'Und das Beste: qtplan ist kostenlos, läuft lokal im Browser, keine Registrierung, keine Objektlimits. Board, Roadmap und Übersicht zeigen dieselben Daten aus drei Perspektiven — kein Doppelpflegen, kein Tool-Wechsel. Multi-Board-Verwaltung trennt Kundenprojekte sauber, Doppelbuchungs-Erkennung zeigt wo sich parallele Aufträge überlappen.',
      ],
    },
    cta: {
      heading: 'Erstes Projekt aufsetzen.',
      body: 'Kein Account. Keine Cloud. Keine Kosten. Alle Daten bleiben lokal im Browser. Board für den Kickoff, Roadmap für den Kunden, Übersicht fürs Angebot.',
    },
  },
  en: {
    title: 'qtplan – Project Planning for Freelancers | Board, Roadmap & PERT',
    metaDesc: 'Board for project kickoff with clients, roadmap for presentations, overview for solid proposals. PERT estimates, canvas, local and free.',
    heroImg: { src: '/img/remote-work.jpg', pos: '50% 65%' },
    hero: {
      h1: 'Kickoff on the board. Roadmap for the client. Proposal with the overview.',
      lead: 'Professional planning without enterprise prices. qtplan provides a board for the project kickoff with the client, a Gantt roadmap for the presentation, and an overview with all consequences for solid proposals. Free, local, no registration.',
    },
    bento: {
      heading: 'Three Views for Freelancers',
      items: [
        {
          span: 2,
          label: 'BOARD',
          title: 'Project Kickoff on the Board — Together with the Client',
          body: 'At the start of every engagement: clarify scope, estimate effort, identify risks. The board is as a free-form canvas — structure tasks, capture PERT estimates, document requirements and consequences. The board can be shown directly in client conversations. What starts as a rough structure iteratively evolves into the project plan instead of being thrown away.',
          variant: 'accent',
        },
        {
          span: 1,
          label: 'ROADMAP',
          title: 'Gantt Timeline That Impresses Clients',
          body: 'Same data as a Gantt timeline — the roadmap for the client. When will what be ready, where are the risks, how do changes affect the plan. Instead of a text list or spreadsheet, an interactive timeline instead of a text list. No extra tool, no double maintenance.',
        },
        {
          span: 1,
          label: 'PROPOSAL',
          title: 'Solid Proposals with the Aggregated Overview',
          body: 'The overview summarizes all requirements, deadlines, and consequences. "With 85% probability the project will take 3 to 5 weeks, the main risk lies in the API integration" — that is professional, traceable, and protects against overly optimistic proposals that lead to unpaid overtime.',
        },
        {
          span: 2,
          label: 'ESTIMATION',
          title: 'PERT Estimates That Build Trust',
          body: 'Clients expect precise timelines — and this is exactly where most freelancers struggle. Without methodology, estimates stay imprecise. qtplan uses the PERT methodology: three estimates per task (optimistic, realistic, pessimistic), from these expected value E = (O + 4M + P) / 6 and uncertainty. Captured on the board, visible in the roadmap, aggregated as risk in the overview. This distinguishes professional consultants from casual freelancers.',
        },
        {
          span: 1,
          label: 'PROJECTS',
          title: 'Multi-Board for Parallel Engagements',
          body: 'Two to three parallel engagements are the norm. Each client gets their own board. Switching takes seconds. Cross-board double-booking detection shows where time periods overlap — before committing to an unrealistic deadline for a client.',
          variant: 'accent',
        },
        {
          span: 1,
          label: 'CANVAS',
          title: 'Visualize Project Structure for Kickoffs',
          body: 'The canvas provides a free-form surface with layers, groups, connections, and a sprite catalog. For kickoffs to collaboratively map out the structure with the client. Tech trees, dependencies, infrastructure — as a living diagram instead of a static slide.',
        },
        {
          span: 2,
          label: 'LOCAL',
          title: 'Free, Local, and Unlimited',
          body: 'qtplan runs entirely in the browser. No cloud, no backend, no registration required, no object limits. Project data and estimates never leave the machine. No monthly costs, no vendor lock-in. Boards can be exported as JSON — as backup or to share with clients.',
          variant: 'muted',
        },
        {
          span: 1,
          label: 'WORKFLOW',
          title: 'Configurable Columns for Your Process',
          body: 'Flexible column structure per board — from Backlog through In Progress to Done, or custom categories like Review, Client Feedback, or Deployment. Each column can be assigned categories, and the task pool filters automatically.',
        },
      ],
    },
    narrative: {
      heading: 'Why Freelancers Need Professional Planning Tools',
      paragraphs: [
        'Freelancers face a paradox: clients expect the same professionalism as from an agency — but the budget for enterprise tools is missing, and these tools deliver features that are irrelevant for individuals. The result: many freelancers plan with note apps, spreadsheets, or not at all.',
        'In qtplan, everything starts on the board. As a free-form canvas — clarify scope with the client, structure tasks, uncover risks. PERT estimates provide a methodically sound foundation for proposals. Instead of "that will take about four weeks," it becomes "with 85% probability the effort is between three and five weeks."',
        'For the client, the same data becomes the Gantt timeline — the roadmap. An interactive schedule instead of static spreadsheets. Shifts and their impact are immediately visible. The aggregated overview summarizes requirements and consequences — as a basis for the proposal or directly in the meeting.',
        'And the best part: qtplan is free, runs locally in the browser, no registration, no object limits. Board, roadmap, and overview show the same data from three perspectives — no double maintenance, no tool switching. Multi-board management cleanly separates client projects, double-booking detection shows where parallel engagements overlap.',
      ],
    },
    cta: {
      heading: 'Set up your first project.',
      body: 'No account. No cloud. No costs. All data stays local in the browser. Board for the kickoff, roadmap for the client, overview for the proposal.',
    },
  },
};

export function ForFreelancerPage({ lang }: { lang: 'de' | 'en' }) {
  return <AudiencePageLayout lang={lang} slug="freelancer" content={content[lang]} />;
}
